import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sportsService, SportsQueryParams } from '@/services/sportsService';
import { CreateSportRequest } from '@/types/model';

export const sportsKeys = {
  all: ['sports'] as const,
  lists: () => [...sportsKeys.all, 'list'] as const,
  list: (params: SportsQueryParams) => [...sportsKeys.lists(), params] as const,
  details: () => [...sportsKeys.all, 'detail'] as const,
  detail: (id: string) => [...sportsKeys.details(), id] as const,
};

export const useSports = (params: SportsQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: sportsKeys.list(params),
    queryFn: () => sportsService.getSports(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useSport = (id: string) => {
  return useQuery({
    queryKey: sportsKeys.detail(id),
    queryFn: () => sportsService.getSportById(id),
    enabled: !!id,
  });
};

export const useCreateSport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sportData: CreateSportRequest) =>
      sportsService.createSport(sportData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sportsKeys.lists() });
    },
  });
};

export const useUpdateSport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSportRequest> }) =>
      sportsService.updateSport(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sportsKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: sportsKeys.lists() });
    },
  });
};

export const useDeleteSport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sportsService.deleteSport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sportsKeys.lists() });
    },
  });
};

export const useToggleSportStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      sportsService.toggleSportStatus(id, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sportsKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: sportsKeys.lists() });
    },
  });
};


